import { OneTrustCookieGroup } from "~/assets/constants/cookie-consent";

export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig();

  useScript(
    {
      key: "one-trust",
      src: "https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js",
      "data-domain-script": runtimeConfig.public.oneTrustDataDomainScript,
      "data-language": "nl",
      type: "text/javascript",
      async: false,
      defer: false,
      fetchpriority: "high",
    },
    {
      tagPriority: "critical",
      // trigger: "server"
    }
  );

  /* As soon as user updates the consent, we process the selected preferences
  and store the accepted cookie categories in root store for application wide checks
  ----------------------------------------------------------------------------------
  */
  function manageUserConsent() {
    // Avoid processing cookie choices and re-updating on page reload
    window.dataLayer = window.dataLayer || [];

    // Store the original push method temporarily
    const originalPush = window.dataLayer.push;

    // Override the push method of dataLayer
    window.dataLayer.push = function (...args) {
      args.forEach((eventObj) => {
        if (eventObj && eventObj.event === "OneTrustGroupsUpdated") {
          const eventGroups = eventObj.OnetrustActiveGroups?.split(",") ?? [];
          const activeGroups = eventGroups.filter(
            (group: string) => group.trim() !== ""
          );
          processConsentChange(activeGroups);
        }
      });

      // Call the original push method to ensure dataLayer behavior remains intact
      return originalPush.apply(window.dataLayer, args);
    };
  }

  if (!import.meta.client) return;
  manageUserConsent();

  // Fire the virtual_page_view event on each page navigation only
  const router = useRouter();
  router.afterEach(async (to, from) => {
    // Integrate with external services like Datadog
    if (nuxtApp.$ddRum) nuxtApp.$ddRum.startView({ name: to.name });
    
    // Avoid firing on the same page on refresh immediately to control delay
    if (to.fullPath === from.fullPath) return;
    await firePageViewEvent(nuxtApp, from.fullPath, to.fullPath);
  });
});

function processConsentChange(activeCookieGroups: OneTrustCookieGroup[]) {
  const hasConsentChoiceMade = computed(
    () => !!useCookie("OptanonAlertBoxClosed").value
  );

  if (!hasConsentChoiceMade.value) return;

  processQueuedEvents();

  // verify user has accpted tracking cookies before loading exponea script @ref: app.vue >> useExponeaScript
  controlExponea(activeCookieGroups);
}

function controlExponea(acceptedGroups: OneTrustCookieGroup[]) {
  const checkAnalyticsConsent = acceptedGroups.includes(
    OneTrustCookieGroup.Analytical
  );
  if (checkAnalyticsConsent) exponeaTrackingConsent.accept();
}

async function firePageViewEvent(
  nuxtApp: any,
  fromPath: string,
  toPath: string
) {
  const { useAuthStore } = await import("~/stores/auth.store");
  const { useRootStore } = await import("~/stores/root.store");
  const authStore = useAuthStore();
  const rootStore = useRootStore();
  const { logPageView } = await import("~/utils/event-logger.utils");
  // Log the virtual_page_view event
  logPageView({
    fromPath,
    toPath,
    isIncVat: rootStore.isIncVat,
    locale: nuxtApp.$i18n.locale.value,
    user: authStore.user,
  });
}
